///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2023 - 2023
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage } from "react-intl";

import messages from "../../../helpers/messages";
import FiltersIcon from "../../icons/FiltersIcon";
import {
  clearRowsSelected,
  resetCapacityMatchesSummaryFilters,
  resetColumnOrder,
  setCopyMatchesFromRow
} from "../../../slices/bidProcurementSlice";
import IconButton from "../../form-controls/IconButton";
import ClearFilterIcon from "../../icons/ClearFilterIcon";
import ResetIcon from "../../icons/ResetIcon";
import ExportIcon from "../../icons/ExportIcon";
import Tooltip from "../../Tooltip";
import AlertIcon from "../../icons/AlertIcon";
import { getMatchesTemplateWithData } from "../../../api/capacityMatch";
import saveFile from "../../../helpers/saveFile";
import Spinner from "../../icons/Spinner";
import Alert from "../../Alert";
import {
  mode as modeType,
  capacityMatchingStatus as capacityMatchingStatusType
} from "../../../helpers/types";

const exportMatchesInitialDetails = {
  isFetching: false,
  isError: false
};

export const Toolbar = ({ openCarrierMatchingDrawer, bidId }) => {
  const dispatch = useDispatch();
  const {
    bidProcurement: {
      rowsSelected,
      copyMatchesFromRow,
      capacityMatchLaneSummary: {
        filtersApplied,
        columns: { changed: columnOrderChanged }
      },
      isCapacityMatchingInProgress,
      matchesLastRanBefore7Days
    },
    user: { user },
    bidSummary: { summary },
    featureFlags: { enableDragAndDropForIcm, enableExportMatches }
  } = useSelector(state => state);

  const [exportMatchesDetails, setExportMatchesDetails] = useState(
    exportMatchesInitialDetails
  );

  const capacityMatchingStatus = summary?.modes.find(
    ({ mode }) => mode === modeType.tl
  )?.capacityMatchingStatus;

  const isInProgressOrReview =
    capacityMatchingStatus === capacityMatchingStatusType.inProgress ||
    capacityMatchingStatus === capacityMatchingStatusType.review;

  const handleExportMatchesDownload = () => {
    if (exportMatchesDetails.isFetching === false) {
      setExportMatchesDetails({
        isFetching: true,
        isError: false
      });
      getMatchesTemplateWithData({
        bidId,
        userRoleForBidWhenManagementUser: user.userRoleForManagementUser
      })
        .then(({ data }) => {
          saveFile(data, `${summary.name} - Carrier Match Suggestions.xlsx`);
          setExportMatchesDetails(exportMatchesInitialDetails);
        })
        .catch(() => {
          setExportMatchesDetails({
            isFetching: false,
            isError: true
          });
        });
    }
  };

  return (
    <div className="ml-24 my-12 h-32" id="toolbar">
      <button
        id="filter"
        className="tertiary-button cursor-pointer filters hidden"
      >
        <FiltersIcon className="align-text-bottom mr-8" />
        {messages.filters.defaultMessage}
      </button>
      <IconButton
        title={messages.clearFilters.defaultMessage}
        disabled={!filtersApplied}
        dataTestId="clear-filter-button"
        onClick={() => dispatch(resetCapacityMatchesSummaryFilters())}
        icon={ClearFilterIcon}
        offset={[7, 6]}
        className="mt-8 ml-8 inline-block"
      />
      {enableDragAndDropForIcm ? (
        <IconButton
          title={messages.resetColumns.defaultMessage}
          disabled={!columnOrderChanged}
          dataTestId="reset-order-button"
          onClick={() => dispatch(resetColumnOrder())}
          icon={ResetIcon}
          offset={[10, 6]}
          className="mt-8 ml-8 inline-block"
        />
      ) : (
        <></>
      )}
      {enableExportMatches && isInProgressOrReview ? (
        <>
          <button
            className={`text-blue ml-12 text-transform-none align-super ${
              isCapacityMatchingInProgress ? "text-light-grey" : ""
            }`}
            style={{ minWidth: 115 }}
            onClick={handleExportMatchesDownload}
            disabled={isCapacityMatchingInProgress}
            data-testid="export-matches-button"
          >
            {exportMatchesDetails.isFetching ? (
              <Spinner
                className="spinner align-middle"
                height="16px"
                width="16px"
                fill="#0088d1"
              />
            ) : (
              <>
                <ExportIcon
                  height={12}
                  width={12}
                  className="mr-8 align-middle"
                  fill={isCapacityMatchingInProgress ? "#ADAFB2" : "#0066FA"}
                />
                {messages.exportCSV.defaultMessage}
                {matchesLastRanBefore7Days ? (
                  <Tooltip
                    content={
                      messages.exportMatchesWarningMessage.defaultMessage
                    }
                  >
                    <span>
                      <AlertIcon
                        height={14}
                        width={16}
                        className="ml-8"
                        data-testid="alert-icon"
                      />
                    </span>
                  </Tooltip>
                ) : (
                  <></>
                )}
              </>
            )}
          </button>
          <Alert
            handleClose={() =>
              setExportMatchesDetails(exportMatchesInitialDetails)
            }
            message={messages.downloadDocumentErrorMessage.defaultMessage}
            open={exportMatchesDetails.isError}
          />
        </>
      ) : (
        <></>
      )}
      {rowsSelected.length || copyMatchesFromRow !== null ? (
        <p
          className="mb-12 weight--semibold font-size-18 inline-block fr"
          id="lanes-selected"
        >
          <FormattedMessage
            {...messages.lanesSelected}
            values={{ noOfLanes: rowsSelected.length }}
          />
          <button
            className="text-button font-size-14 mx-8 ml-32 h-32 cursor-pointer"
            data-testid="cancel"
            style={{ minWidth: "91px" }}
            onClick={() => {
              dispatch(clearRowsSelected());
              if (copyMatchesFromRow !== null)
                dispatch(setCopyMatchesFromRow(null));
            }}
          >
            {messages.cancel.defaultMessage}
          </button>
          {copyMatchesFromRow ? (
            <button
              className="text-button font-size-14 mx-8 mr-32 h-32 bg-cobalt-blue text-white blue-button cursor-pointer"
              data-testid="copy-matches"
              style={{ minWidth: "126px" }}
              disabled={rowsSelected.length === 0}
              onClick={() => openCarrierMatchingDrawer(false)}
            >
              {messages.copyMatches.defaultMessage}
            </button>
          ) : (
            <button
              className="text-button font-size-14 mx-8 mr-32 h-32 bg-cobalt-blue text-white blue-button cursor-pointer"
              data-testid="bulk-matching"
              style={{ minWidth: "126px" }}
              disabled={rowsSelected.length <= 1}
              onClick={() => openCarrierMatchingDrawer(true)}
            >
              {messages.bulkMatching.defaultMessage}
            </button>
          )}
        </p>
      ) : null}
    </div>
  );
};

export default Toolbar;
